/**
 * segmentReordering.ts - Utilities for reordering segment nodes within a switch
 *
 * Segment nodes are children of switch nodes and appear horizontally.
 * This module provides functions to reorder segments while ensuring
 * the default segment always stays rightmost.
 *
 * Related:
 *   - utils/nodeReordering.ts - General node reordering
 *   - nodes/SegmentNode.tsx - Segment node component
 */

import type { Edge, Node } from '@xyflow/react';
import type { BaseNodeData } from '../types';
import type { BranchEdgeData } from '../types/edges';
import type { SegmentNodeData } from '../types/stepNodes';
import { NODE_TYPES } from '../types';
import { isSwitchNodeData } from '../types/typeGuards';

/**
 * Get all segment nodes for a given switch node, ordered by segmentIndex
 *
 * Note: Uses the segment's parentSwitchNodeId data property to find segments,
 * not edge sources. This is because branch edges use T-junction visual style
 * where the source is the step BEFORE the switch, not the switch itself.
 */
export function getOrderedSegments(
	parentSwitchNodeId: string,
	nodes: Node<BaseNodeData>[],
	_edges: Edge[]
): Node<SegmentNodeData>[] {
	// Find segment nodes by their parentSwitchNodeId data property
	const segmentNodes = nodes.filter(
		n => n.type === NODE_TYPES.SEGMENT && (n.data as SegmentNodeData).parentSwitchNodeId === parentSwitchNodeId
	) as Node<SegmentNodeData>[];

	// Sort by segmentIndex
	return segmentNodes.sort((a, b) => (a.data.segmentIndex ?? 0) - (b.data.segmentIndex ?? 0));
}

export function applyLocalSegmentOrder(
	parentSwitchNodeId: string,
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	orderedSegmentIds: string[]
): {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
} {
	const segmentNodes = getOrderedSegments(parentSwitchNodeId, nodes, edges);
	const segmentById = new Map(segmentNodes.map(node => [node.id, node] as const));
	const newIndexBySegmentId = new Map<string, number>();
	const newIndexByCaseId = new Map<string, number>();

	orderedSegmentIds.forEach((segmentId, index) => {
		newIndexBySegmentId.set(segmentId, index);
		const caseId = segmentById.get(segmentId)?.data.caseId;
		if (caseId) {
			newIndexByCaseId.set(caseId, index);
		}
	});

	const updatedNodes = nodes.map(node => {
		if (node.type === NODE_TYPES.SEGMENT) {
			const segData = node.data as SegmentNodeData;
			if (segData.parentSwitchNodeId !== parentSwitchNodeId) {
				return node;
			}

			const newIndex = newIndexBySegmentId.get(node.id);
			if (newIndex === undefined || newIndex === segData.segmentIndex) {
				return node;
			}

			return {
				...node,
				data: {
					...segData,
					segmentIndex: newIndex,
				},
			};
		}

		if (node.id === parentSwitchNodeId && isSwitchNodeData(node.data)) {
			const existingCases = node.data.automationStep.cases || [];
			const reorderedCases = orderedSegmentIds
				.map(segmentId => segmentById.get(segmentId)?.data.caseId)
				.filter((caseId): caseId is string => !!caseId)
				.map(caseId => existingCases.find(caseStatement => caseStatement.id === caseId))
				.filter((caseStatement): caseStatement is NonNullable<(typeof existingCases)[number]> => !!caseStatement);

			if (reorderedCases.length === existingCases.length) {
				return {
					...node,
					data: {
						...node.data,
						automationStep: {
							...node.data.automationStep,
							cases: reorderedCases,
						},
					},
				};
			}
		}

		return node;
	});

	const updatedEdges = edges.map(edge => {
		if (edge.type !== 'branch') {
			return edge;
		}

		const edgeData = edge.data as BranchEdgeData | undefined;
		const belongsToSwitch = edgeData?.switchNodeId === parentSwitchNodeId || edge.source === parentSwitchNodeId;
		if (!belongsToSwitch) {
			return edge;
		}

		const newIndex =
			newIndexBySegmentId.get(edge.target) ?? (edgeData?.caseId ? newIndexByCaseId.get(edgeData.caseId) : undefined);
		if (newIndex === undefined || edgeData?.caseIndex === newIndex) {
			return edge;
		}

		return {
			...edge,
			data: {
				...edgeData,
				caseIndex: newIndex,
				totalCases: orderedSegmentIds.length,
			},
		};
	});

	return {
		nodes: updatedNodes,
		edges: updatedEdges,
	};
}
